/**
 * Google Analytics 4 Event Tracking
 * Śledzenie interakcji użytkowników na stronie
 */

// Deklaracja typu dla gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Wysyła event do Google Analytics 4
 */
export const trackEvent = (
  eventName: string,
  eventParams?: {
    [key: string]: any;
  }
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Śledzi obliczenie w kalkulatorze
 */
export const trackCalculation = (data: {
  income: number;
  costs: number;
  taxForm: string;
  zusType: string;
  pkdCode?: string;
  isVatPayer?: boolean;
  calculationTime?: number;
}) => {
  trackEvent('calculation_completed', {
    event_category: 'Calculator',
    event_label: data.taxForm,
    value: data.income,
    income: data.income,
    costs: data.costs,
    tax_form: data.taxForm,
    zus_type: data.zusType,
    pkd_code: data.pkdCode || 'unknown',
    is_vat_payer: data.isVatPayer || false,
    calculation_time_ms: data.calculationTime || 0,
  });
};

/**
 * Śledzi porównanie form opodatkowania
 */
export const trackTaxFormComparison = (data: {
  income: number;
  costs: number;
  formsCompared: string[];
  bestForm?: string;
}) => {
  trackEvent('tax_form_comparison', {
    event_category: 'Calculator',
    event_label: 'Tax Form Comparison',
    value: data.income,
    income: data.income,
    costs: data.costs,
    forms_compared: data.formsCompared.join(','),
    best_form: data.bestForm || 'unknown',
  });
};

/**
 * Śledzi wiadomość w czatbocie
 */
export const trackChatMessage = (data: {
  messageLength: number;
  isFirstMessage: boolean;
  responseTime?: number;
  hasError?: boolean;
}) => {
  trackEvent('chat_message_sent', {
    event_category: 'Chatbot',
    event_label: data.isFirstMessage ? 'First Message' : 'Follow-up',
    value: data.messageLength,
    message_length: data.messageLength,
    is_first_message: data.isFirstMessage,
    response_time_ms: data.responseTime || 0,
    has_error: data.hasError || false,
  });
};

/**
 * Śledzi otwarcie czatbota
 */
export const trackChatbotOpen = () => {
  trackEvent('chatbot_opened', {
    event_category: 'Chatbot',
    event_label: 'Chatbot Opened',
  });
};

/**
 * Śledzi zamknięcie czatbota
 */
export const trackChatbotClose = (data: {
  messagesCount: number;
  sessionDuration: number;
}) => {
  trackEvent('chatbot_closed', {
    event_category: 'Chatbot',
    event_label: 'Chatbot Closed',
    messages_count: data.messagesCount,
    session_duration_seconds: data.sessionDuration,
  });
};

/**
 * Śledzi analizę AI
 */
export const trackAIAnalysis = (data: {
  income: number;
  costs: number;
  taxForm: string;
  analysisTime?: number;
}) => {
  trackEvent('ai_analysis_completed', {
    event_category: 'AI Assistant',
    event_label: 'AI Analysis',
    value: data.income,
    income: data.income,
    costs: data.costs,
    tax_form: data.taxForm,
    analysis_time_ms: data.analysisTime || 0,
  });
};

/**
 * Śledzi nawigację między sekcjami
 */
export const trackPageView = (page: string, title?: string) => {
  trackEvent('page_view', {
    event_category: 'Navigation',
    event_label: page,
    page_title: title || page,
    page_path: window.location.pathname + window.location.hash,
  });
  
  // Również standardowy page_view dla GA4
  if (window.gtag) {
    window.gtag('config', 'G-PXC076RSZY', {
      page_path: window.location.pathname + window.location.hash,
      page_title: title || document.title,
    });
  }
};

/**
 * Śledzi kliknięcie w przycisk/akcję
 */
export const trackButtonClick = (buttonName: string, location?: string) => {
  trackEvent('button_click', {
    event_category: 'User Interaction',
    event_label: buttonName,
    button_name: buttonName,
    button_location: location || 'unknown',
  });
};

/**
 * Śledzi wyszukiwanie PKD
 */
export const trackPKDSearch = (data: {
  query: string;
  resultsCount: number;
  selected?: boolean;
}) => {
  trackEvent('pkd_search', {
    event_category: 'PKD Search',
    event_label: data.selected ? 'PKD Selected' : 'PKD Searched',
    search_query: data.query,
    results_count: data.resultsCount,
    pkd_selected: data.selected || false,
  });
};

/**
 * Śledzi otwarcie modala
 */
export const trackModalOpen = (modalName: string) => {
  trackEvent('modal_opened', {
    event_category: 'User Interaction',
    event_label: modalName,
    modal_name: modalName,
  });
};

/**
 * Śledzi zamknięcie modala
 */
export const trackModalClose = (modalName: string, duration?: number) => {
  trackEvent('modal_closed', {
    event_category: 'User Interaction',
    event_label: modalName,
    modal_name: modalName,
    duration_seconds: duration || 0,
  });
};

/**
 * Śledzi czas spędzony na stronie
 */
export const trackTimeOnPage = (seconds: number, page: string) => {
  trackEvent('time_on_page', {
    event_category: 'Engagement',
    event_label: page,
    value: Math.round(seconds),
    time_on_page_seconds: Math.round(seconds),
  });
};

/**
 * Śledzi scroll depth (głębokość przewinięcia)
 */
export const trackScrollDepth = (depth: number) => {
  trackEvent('scroll_depth', {
    event_category: 'Engagement',
    event_label: `${depth}%`,
    scroll_depth_percent: depth,
  });
};

/**
 * Śledzi błąd w aplikacji
 */
export const trackError = (error: Error, context?: string) => {
  trackEvent('error_occurred', {
    event_category: 'Error',
    event_label: error.message,
    error_message: error.message,
    error_context: context || 'unknown',
    error_stack: error.stack?.substring(0, 500) || '',
  });
};

/**
 * Inicjalizuje śledzenie scroll depth
 */
export const initScrollTracking = () => {
  if (typeof window === 'undefined') return;
  
  let maxScroll = 0;
  const scrollThresholds = [25, 50, 75, 90, 100];
  const trackedThresholds = new Set<number>();
  
  const handleScroll = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollPercent = Math.round((scrollTop + windowHeight) / documentHeight * 100);
    
    if (scrollPercent > maxScroll) {
      maxScroll = scrollPercent;
      
      scrollThresholds.forEach(threshold => {
        if (scrollPercent >= threshold && !trackedThresholds.has(threshold)) {
          trackedThresholds.add(threshold);
          trackScrollDepth(threshold);
        }
      });
    }
  };
  
  window.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
};

/**
 * Inicjalizuje śledzenie czasu na stronie
 */
export const initTimeTracking = (page: string) => {
  if (typeof window === 'undefined') return;
  
  const startTime = Date.now();
  
  const trackTime = () => {
    const timeSpent = (Date.now() - startTime) / 1000;
    trackTimeOnPage(timeSpent, page);
  };
  
  // Śledź czas przy opuszczeniu strony
  window.addEventListener('beforeunload', trackTime);
  
  // Śledź czas co 30 sekund
  const interval = setInterval(() => {
    const timeSpent = (Date.now() - startTime) / 1000;
    if (timeSpent >= 30) {
      trackTimeOnPage(timeSpent, page);
    }
  }, 30000);
  
  return () => {
    clearInterval(interval);
    window.removeEventListener('beforeunload', trackTime);
  };
};


